
import { useState, useEffect } from 'react';
// @ts-ignore
import uploadService from '../../../services/uploadService';

interface UploadFile {
  _id: string;
  originalFileName: string;
  storedFileName: string;
  bucketName: string;
  filePath: string;
  publicUrl: string;
  contentType: string;
  size: number;
  folder: string;
  fileObjectId?: string;
  fileObjectType?: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  fullUrl: string;
}

const VIDEO_EXTENSIONS = /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i;

const GalleryManagement = () => {
  const [images, setImages] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 8,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  const [selectedFolder, setSelectedFolder] = useState('all');
  const folders = ['all', 'Banner', 'Logo', 'News', 'Projects', 'Gallery', 'Users', 'Images'];

  useEffect(() => {
    fetchImages();
  }, [selectedFolder, pagination.page]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (selectedFolder !== 'all') {
        params.folder = selectedFolder.toLowerCase();
      }

      const response = await uploadService.getList(params);
      
      if (response.success && response.data) {
        setImages(response.data.items || []);
        setPagination({
          page: response.data.page || pagination.page,
          limit: response.data.limit || pagination.limit,
          total: response.data.total || 0,
          totalPages: response.data.totalPages || 0,
          hasNext: response.data.hasNext || false,
          hasPrev: response.data.hasPrev || false
        });
      }
    } catch (error) {
      console.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN');
  };

  const handleBackup = async () => {
    if (!window.confirm('Bạn có chắc chắn muốn backup tất cả files từ MinIO? Quá trình này có thể mất nhiều thời gian.')) {
      return;
    }

    setBackupLoading(true);
    try {
      await uploadService.backupFiles();
      alert('Backup thành công! File đã được tải xuống.');
    } catch (error: any) {
      console.error('Backup error:', error);
      alert(`Lỗi khi backup: ${error.message || 'Lỗi không xác định'}`);
    } finally {
      setBackupLoading(false);
    }
  };

  const filteredImages = selectedFolder === 'all' 
    ? images 
    : images.filter(image => image.folder.toLowerCase() === selectedFolder.toLowerCase());

  const isVideoFileType = (file: UploadFile) => {
    if (file.contentType?.startsWith('video/')) return true;
    return VIDEO_EXTENSIONS.test(file.fullUrl || '');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Thư viện ảnh</h2>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            Tổng: {pagination.total} hình ảnh
          </div>
          <button
            onClick={handleBackup}
            disabled={backupLoading}
            className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
              backupLoading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-[#00C897] hover:bg-[#00C897]/90 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {backupLoading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Đang backup...
              </>
            ) : (
              <>
                <i className="ri-download-2-line"></i>
                Backup Files
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Lọc theo folder:</span>
          <div className="flex flex-wrap gap-2">
            {folders.map(folder => (
              <button
                key={folder}
                onClick={() => {
                  setSelectedFolder(folder);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className={`px-3 py-1 rounded-full text-sm whitespace-nowrap cursor-pointer ${
                  selectedFolder === folder
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {folder === 'all' ? 'Tất cả' : folder}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-500">Đang tải...</div>
        </div>
      )}

      {/* Gallery Grid */}
      {!loading && filteredImages.length === 0 && (
        <div className="flex justify-center items-center py-12">
          <div className="text-gray-500">Không có hình ảnh nào</div>
        </div>
      )}

      {!loading && filteredImages.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map((image) => (
              <div key={image._id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="relative w-full h-48 bg-black">
                  {isVideoFileType(image) ? (
                    <video
                      src={image.fullUrl}
                      muted
                      controls
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <img
                      src={image.fullUrl}
                      alt={image.originalFileName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/300x200?text=No+Image';
                      }}
                    />
                  )}
                  {isVideoFileType(image) && (
                    <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center">
                      <i className="ri-play-circle-line"></i>
                    </div>
                  )}
                </div>
                
                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {image.fileObjectType && (
                        <span className="inline-flex px-2 py-1 text-xs bg-teal-100 text-teal-800 rounded">
                          {image.fileObjectType}
                        </span>
                      )}
                      {image.contentType && (
                        <span className="inline-flex px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                          {image.contentType}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500">
                      {formatFileSize(image.size)}
                    </span>
                  </div>

                  <div className="text-xs text-gray-500">
                    {formatDate(image.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Trang {pagination.page} / {pagination.totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrev}
                    className={`px-3 py-1 rounded-md text-sm cursor-pointer ${
                      pagination.hasPrev
                        ? 'bg-teal-600 text-white hover:bg-teal-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Trước
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNext}
                    className={`px-3 py-1 rounded-md text-sm cursor-pointer ${
                      pagination.hasNext
                        ? 'bg-teal-600 text-white hover:bg-teal-700'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Sau
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GalleryManagement;
